import React, { useState } from "react";
import { Button, Input, List, Modal, Space, Typography, message } from "antd";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { isValidSlug, slugify } from "./slug";
import { createCategory, deleteCategory, errorMessage, updateCategory } from "./postsApi";

const { Text } = Typography;

const EMPTY = { name: "", slug: "" };

/**
 * Post categories for one language.
 *
 * Categories are a collection, not page structure, so unlike the marketing
 * pages these can be freely added and removed. They are per language — a
 * category id belongs to one language's document — so this edits the language
 * the list is currently showing.
 */
export default function CategoriesModal({ lang, categories, onClose, onChanged }) {
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const submit = async () => {
    const name = draft.name.trim();
    const slug = (draft.slug || slugify(name)).trim();

    if (!name) return message.error("Категорията се нуждае от име");
    if (!isValidSlug(slug))
      return message.error("Адресът (slug) трябва да е с малки латински букви, разделени с тире");

    setIsBusy(true);
    try {
      if (editingId) {
        await updateCategory(editingId, { name, slug });
        message.success("Категорията е обновена");
      } else {
        await createCategory({ name, slug, lang });
        message.success("Категорията е добавена");
      }
      reset();
      onChanged();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const remove = async (category) => {
    setIsBusy(true);
    try {
      await deleteCategory(category._id);
      message.success("Категорията е изтрита");
      onChanged();
    } catch (error) {
      // The API refuses while posts still reference it and says how many, which
      // is more useful than anything this dialog could invent.
      message.error(errorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      open
      title={`Категории (${lang.toUpperCase()})`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Затвори</Button>}
    >
      <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
        <Input
          value={draft.name}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              name: event.target.value,
              // Track the name until the slug is typed into directly, same rule
              // as the post editor.
              slug: current.slugTouched ? current.slug : slugify(event.target.value),
            }))
          }
          placeholder="Име на категорията"
          style={CONTROL}
          maxLength={200}
        />
        <Input
          value={draft.slug}
          onChange={(event) =>
            setDraft((current) => ({ ...current, slug: event.target.value, slugTouched: true }))
          }
          placeholder="primer-kategoriya"
          style={CONTROL}
          maxLength={200}
          status={draft.slug && !isValidSlug(draft.slug) ? "error" : undefined}
        />
        <Space wrap>
          <Button type="primary" onClick={submit} loading={isBusy} style={CONTROL}>
            {editingId ? "Запази категорията" : "Добави категория"}
          </Button>
          {editingId && <Button onClick={reset}>Отказ</Button>}
        </Space>
      </Space>

      <List
        size="small"
        bordered
        dataSource={categories}
        locale={{ emptyText: "Все още няма категории" }}
        renderItem={(category) => (
          <List.Item
            actions={[
              <Button
                key="edit"
                type="link"
                onClick={() => {
                  setEditingId(category._id);
                  setDraft({ name: category.name, slug: category.slug, slugTouched: true });
                }}
              >
                Редактирай
              </Button>,
              <Button key="delete" type="link" danger onClick={() => remove(category)}>
                Изтрий
              </Button>,
            ]}
          >
            <Space direction="vertical" size={0}>
              <span>{category.name}</span>
              <Text type="secondary">/kat/{category.slug}/</Text>
            </Space>
          </List.Item>
        )}
      />
    </Modal>
  );
}
